import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users, Plus, Edit2, Shield, ShieldCheck, UserX, Mail, User, Search, Building2 } from "lucide-react";

export default function UsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    business_id: "",
  });

  useEffect(() => {
    // Check if user is admin
    if (currentUser && currentUser.role !== "admin") {
      toast.error(t("usr.toastAdminRequired"));
      navigate("/");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, bizRes] = await Promise.all([
        api.get("/users"),
        api.get("/businesses"),
      ]);
      setUsers(usersRes.data);
      setBusinesses(bizRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error(t("usr.toastLoadFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const getBusinessName = (businessId) => {
    const biz = businesses.find(b => b.id === businessId);
    return biz ? biz.name : null;
  };

  const resetForm = () => {
    setForm({ name: "", email: "", password: "", role: "user", business_id: "" });
    setEditingUser(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "user",
      business_id: user.business_id || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (editingUser) {
      // Update existing user
      try {
        const updateData = { name: form.name, role: form.role, business_id: form.business_id || null };
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success(t("usr.toastUpdated"));
        setDialogOpen(false);
        resetForm();
        fetchData();
      } catch (e) {
        toast.error(t("usr.toastUpdateFailed"));
      }
    } else {
      // Create new user
      if (!form.email || !form.password || !form.name) {
        toast.error(t("usr.toastRequired"));
        return;
      }
      // Regular users must have a business assigned
      if (form.role === "user" && !form.business_id) {
        toast.error(t("usr.toastNeedBusiness"));
        return;
      }
      try {
        await api.post("/auth/register", {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          business_id: form.role === "user" ? form.business_id : null,
        });
        toast.success(t("usr.toastCreated"));
        setDialogOpen(false);
        resetForm();
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || t("usr.toastCreateFailed"));
      }
    }
  };

  const toggleUserStatus = async (user) => {
    const newStatus = user.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/users/${user.id}`, { status: newStatus });
      toast.success(newStatus === "active" ? t("usr.toastActivated") : t("usr.toastDeactivated"));
      fetchData();
    } catch (e) {
      toast.error(t("usr.toastStatusFailed"));
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    switch (role) {
      case "admin":
        return <Badge className="bg-[#BF5AF2]/20 text-[#BF5AF2] border-[#BF5AF2]/30"><ShieldCheck className="w-3 h-3 mr-1" />Admin</Badge>;
      default:
        return <Badge variant="outline" className="border-border text-muted-foreground"><Shield className="w-3 h-3 mr-1" />User</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("usr.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("usr.subtitle")}</p>
        </div>
        <Button
          data-testid="create-user-btn"
          onClick={openCreateDialog}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("usr.addUser")}
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("usr.searchPh")}
            className="pl-10 bg-secondary/50 border-transparent"
          />
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingUser ? t("usr.editUser") : t("usr.createUser")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("usr.fullName")}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Doe"
                className="bg-secondary/50 border-transparent"
              />
            </div>
            {!editingUser && (
              <>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="user@example.com"
                    className="bg-secondary/50 border-transparent"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••"
                    className="bg-secondary/50 border-transparent"
                  />
                </div>
              </>
            )}
            <div>
              <Label>{t("usr.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-secondary/50 border-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t("usr.roleUser")}</SelectItem>
                  <SelectItem value="admin">{t("usr.roleAdmin")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {t("usr.roleHint")}
              </p>
            </div>
            {form.role === "user" && (
              <div>
                <Label>{t("usr.assignedBusiness")}</Label>
                <Select value={form.business_id} onValueChange={(v) => setForm({ ...form, business_id: v })}>
                  <SelectTrigger className="bg-secondary/50 border-transparent">
                    <SelectValue placeholder={t("usr.selectBusiness")} />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map(biz => (
                      <SelectItem key={biz.id} value={biz.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3" />
                          {biz.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  {t("usr.businessHint")}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("usr.cancel")}</Button>
              <Button onClick={handleSave} className="bg-primary text-primary-foreground">
                {editingUser ? t("usr.updateUser") : t("usr.createUserBtn")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t("usr.emptyTitle")}</h3>
            <p className="text-sm text-muted-foreground">
              {users.length === 0 ? t("usr.emptyCreate") : t("usr.emptySearch")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="bg-white border-gray-200 hover:border-primary/20 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">{user.name}</h3>
                        {getRoleBadge(user.role)}
                        {user.status === "inactive" && (
                          <Badge variant="outline" className="border-destructive/30 text-destructive">{t("usr.inactive")}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </div>
                      {user.business_id && getBusinessName(user.business_id) && (
                        <div className="flex items-center gap-1 text-xs text-primary mt-1">
                          <Building2 className="w-3 h-3" />
                          {getBusinessName(user.business_id)}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {t("usr.joined")} {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(user)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleUserStatus(user)}
                        className={user.status === "active" ? "text-destructive hover:text-destructive" : "text-primary hover:text-primary"}
                      >
                        <UserX className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 flex gap-4 text-sm text-muted-foreground">
        <span>{users.length} {t("usr.statsTotal")}</span>
        <span>{users.filter(u => u.role === "admin").length} {t("usr.statsAdmins")}</span>
        <span>{users.filter(u => u.status === "active").length} {t("usr.statsActive")}</span>
      </div>
    </div>
  );
}
